import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import { bugHunterApi } from "../services/api";
import { detailText } from "../utils/apiErrors";

const SEV_STYLES = {
  low:    "bg-blue-50  border-blue-200  text-blue-800",
  medium: "bg-amber-50 border-amber-200 text-amber-800",
  high:   "bg-red-50   border-red-200   text-red-800",
};

const SEV_LABEL = { low: "נמוך", medium: "בינוני", high: "גבוה" };

function fmt(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("he-IL", { dateStyle: "short" });
}

export default function BugHunterPage() {
  const [days, setDays] = useState(30);
  const [minOcc, setMinOcc] = useState(2);
  const [runParams, setRunParams] = useState(null);
  const [ticketMsg, setTicketMsg] = useState(null);

  const { data, isLoading, isFetching, isError, error, refetch } = useQuery({
    queryKey: ["bug-hunter", runParams],
    queryFn: () => bugHunterApi.scan({ days: runParams?.days || days, min_occurrences: runParams?.minOcc || minOcc }),
    enabled: !!runParams,
  });

  const ticketMutation = useMutation({
    // The parameters of the scan that is ON SCREEN, not the form's current values (changing the window after a scan
    // and pressing this button used to file a ticket built from a different scan than the one being looked at).
    mutationFn: () => bugHunterApi.createTicket({ days: runParams.days, min_occurrences: runParams.minOcc }),
    onSuccess: (res) => setTicketMsg(
      res.ticket_created === false
        ? `קריאה #${res.ticket_number} הקיימת עדיין פתוחה — לא נוצרה קריאה כפולה`
        : `קריאה #${res.ticket_number} נוצרה (${res.finding_count} דפוסים)`
    ),
    onError: (err) => setTicketMsg(`שגיאה: ${detailText(err?.response?.data?.detail, "שגיאה ביצירת קריאה")}`),
  });

  const handleScan = (e) => {
    e.preventDefault();
    // Same parameters again: the query key is unchanged, so setRunParams alone would not refetch (a failed scan
    // could not be retried, a successful one never refreshed).
    if (runParams && runParams.days === days && runParams.minOcc === minOcc) refetch();
    else setRunParams({ days, minOcc });
    setTicketMsg(null);
  };

  const findings = data?.findings || [];

  return (
    <div className="max-w-5xl mx-auto space-y-5" dir="rtl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">ציד באגים 🐛</h1>
          <p className="text-sm text-gray-500 mt-0.5">זיהוי דפוסי תקלות חוזרות מקריאות IT</p>
        </div>
      </div>

      {/* Scan controls */}
      <form onSubmit={handleScan} className="bg-white rounded-xl shadow-sm border p-5 flex flex-wrap gap-4 items-end">
        <div>
          <label className="block text-xs text-gray-500 mb-1">חלון זמן (ימים)</label>
          <select value={days} onChange={(e) => setDays(parseInt(e.target.value, 10))}
            className="border rounded-lg px-3 py-2 text-sm">
            {[7, 14, 30, 60, 90].map((d) => (
              <option key={d} value={d}>{d} ימים</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">מינימום הופעות</label>
          <input type="number" min="2" max="50" value={minOcc}
            onChange={(e) => setMinOcc(Math.min(50, Math.max(2, parseInt(e.target.value, 10) || 2)))}   // the API allows 1-50
            className="w-24 border rounded-lg px-3 py-2 text-sm" />
        </div>
        <button type="submit"
          className="bg-brand-600 hover:bg-brand-700 text-white px-5 py-2 rounded-lg text-sm font-medium transition-colors">
          {isLoading || isFetching ? "סורק..." : "סרוק עכשיו"}
        </button>
        {data && findings.length > 0 && (
          <button type="button"
            onClick={() => ticketMutation.mutate()}
            disabled={ticketMutation.isPending}
            className="border border-red-300 text-red-700 hover:bg-red-50 px-4 py-2 rounded-lg text-sm disabled:opacity-50">
            {ticketMutation.isPending ? "יוצר..." : "צור קריאת Bug Report"}
          </button>
        )}
      </form>

      {isError && (
        <div role="alert" className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-2 text-sm">
          {`הסריקה נכשלה: ${detailText(error?.response?.data?.detail, "שגיאה בטעינת הנתונים")}`}
        </div>
      )}

      {ticketMsg && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-4 py-2 text-sm">
          {ticketMsg}
        </div>
      )}

      {/* Results */}
      {data && (
        <div className="space-y-4">
          <div className="text-sm text-gray-500">
            {findings.length === 0
              ? `לא נמצאו דפוסים חוזרים מתוך ${data.total_scanned} קריאות פתוחות`
              : `${findings.length} דפוסים חוזרים מתוך ${data.total_scanned} קריאות פתוחות (${data.lookback_days} ימים)`}
          </div>

          {findings.map((f, i) => (
            <div key={i} className={`border rounded-xl p-4 ${SEV_STYLES[f.severity] || SEV_STYLES.low}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-medium uppercase px-2 py-0.5 rounded-full border">
                      {SEV_LABEL[f.severity]}
                    </span>
                    {f.category && (
                      <span className="text-xs bg-white/60 border rounded px-1.5 py-0.5">{f.category}</span>
                    )}
                    <span className="text-xs opacity-70">{f.occurrences} הופעות</span>
                  </div>
                  <p className="font-medium text-sm">{f.sample_title}</p>
                  {f.company_names.length > 0 && (
                    <p className="text-xs mt-1 opacity-75">חברות: {f.company_names.slice(0, 4).join(", ")}</p>
                  )}
                </div>
                <div className="text-xs opacity-60 whitespace-nowrap">
                  {fmt(f.earliest)} — {fmt(f.latest)}
                </div>
              </div>

              <div className="mt-2 p-2 bg-white/50 rounded-lg text-xs">
                <strong>פעולה מוצעת:</strong> {f.suggested_action}
              </div>

              {f.ticket_numbers.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {f.ticket_numbers.slice(0, 8).map((n, idx) => {
                    const tid = f.ticket_ids?.[idx];
                    return tid ? (
                      <Link key={n} to={`/tickets/${tid}`}
                        className="text-xs bg-white/60 border rounded px-1.5 py-0.5 font-mono hover:bg-white hover:border-gray-400 transition-colors">
                        #{n}
                      </Link>
                    ) : (
                      <span key={n} className="text-xs bg-white/60 border rounded px-1.5 py-0.5 font-mono">
                        #{n}
                      </span>
                    );
                  })}
                  {f.ticket_numbers.length > 8 && (
                    <span className="text-xs opacity-60">+{f.ticket_numbers.length - 8} נוספות</span>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {!data && !isLoading && (
        <div className="bg-white rounded-xl shadow-sm border p-16 text-center text-gray-400">
          <p className="text-3xl mb-3">🐛</p>
          <p>לחץ על "סרוק עכשיו" כדי לזהות דפוסי תקלות חוזרות</p>
        </div>
      )}
    </div>
  );
}
